import workersData from "../data/workers.json";
import teamsData from "../data/teams.json";
import type { BrandName } from "@/components/icons/brand-logo";

export type ToolUsage = {
  name: string;
  count: number;
};

export type ExecutionEconomics = {
  efficiencyGrade: string | null;
  intentAchievement: number | null;
  totalCostUsd: number | null;
  avgCostPerExecution: number | null;
};

export type WorkerStats = {
  totalExecutions: number;
  tasksCompleted: number;
  tasksFailed: number;
  completionRate: number;
  avgCostPerTask: number;
  avgDurationMs: number;
  totalTokens: number;
  customersDeployed: number;
  totalToolCalls: number;
  successfulToolCalls: number;
};

export type PublicWorker = {
  slug: string;
  name: string;
  role: string;
  shortDescription: string | null;
  humanEquivalent: string | null;
  department: string;
  category: string;
  avatar: string;
  availability: string;
  layer: number | null;
  model: string | null;
  tools: string[];
  toolDescriptions: Record<string, string>;
  topToolsUsed: ToolUsage[];
  capabilities: string[];
  skills: string[];
  specializations: string[];
  teams: string[];
  stats: WorkerStats | null;
  executionEconomics: ExecutionEconomics | null;
};

export type TeamStats = {
  totalExecutions: number;
  tasksCompleted: number;
  completionRate: number;
  totalTokens: number;
  estimatedCost: number;
};

export type PublicTeam = {
  slug: string;
  name: string;
  description: string;
  specializations: string[];
  coordinationPattern: string;
  memberCount: number;
  members: string[];
  leader: string | null;
  stats: TeamStats;
};

export type PublicChatModel = {
  name: string;
  provider: string;
  brand: BrandName;
  family: string;
  capabilities: string[];
  popular?: boolean;
};

export const AVAILABLE_CHAT_MODELS: PublicChatModel[] = [
  {
    name: "GPT-5.4 Pro",
    provider: "OpenAI",
    brand: "openai",
    family: "GPT",
    capabilities: ["reasoning", "tool calling"],
    popular: true,
  },
  {
    name: "GPT-5.4",
    provider: "OpenAI",
    brand: "openai",
    family: "GPT",
    capabilities: ["reasoning", "tool calling"],
    popular: true,
  },
  {
    name: "GPT-5.3 Chat",
    provider: "OpenAI",
    brand: "openai",
    family: "GPT",
    capabilities: ["chat", "tool calling"],
    popular: true,
  },
  {
    name: "GPT-5.3 Codex",
    provider: "OpenAI",
    brand: "openai",
    family: "Codex",
    capabilities: ["code", "tool calling"],
    popular: true,
  },
  {
    name: "GPT-5.2",
    provider: "OpenAI",
    brand: "openai",
    family: "GPT",
    capabilities: ["reasoning", "tool calling"],
    popular: true,
  },
  {
    name: "GPT-5.4 Mini",
    provider: "OpenAI",
    brand: "openai",
    family: "GPT",
    capabilities: ["fast", "tool calling"],
  },
  {
    name: "GPT-5.4 Nano",
    provider: "OpenAI",
    brand: "openai",
    family: "GPT",
    capabilities: ["low latency", "tool calling"],
  },
  {
    name: "Gemini 3.1 Pro Preview",
    provider: "Google",
    brand: "gemini",
    family: "Gemini",
    capabilities: ["reasoning", "vision"],
  },
  {
    name: "DeepSeek V3.2",
    provider: "DeepSeek",
    brand: "deepseek",
    family: "DeepSeek",
    capabilities: ["reasoning", "tool calling"],
  },
  {
    name: "Kimi K2.6",
    provider: "Moonshot",
    brand: "moonshot",
    family: "Kimi",
    capabilities: ["long context", "tool calling"],
  },
  {
    name: "Kimi K2.5",
    provider: "Moonshot",
    brand: "moonshot",
    family: "Kimi",
    capabilities: ["long context", "tool calling"],
  },
  {
    name: "GPT-OSS 120B",
    provider: "OpenAI OSS",
    brand: "openai",
    family: "GPT OSS",
    capabilities: ["open weights", "tool calling"],
  },
  {
    name: "DeepSeek V4 Flash",
    provider: "DeepSeek",
    brand: "deepseek",
    family: "DeepSeek",
    capabilities: ["fast", "tool calling"],
  },
  {
    name: "Grok 4.1 Fast",
    provider: "xAI",
    brand: "xai",
    family: "Grok",
    capabilities: ["fast", "tool calling"],
  },
  {
    name: "Grok 4.1 Fast (Reasoning)",
    provider: "xAI",
    brand: "xai",
    family: "Grok",
    capabilities: ["reasoning", "tool calling"],
  },
  {
    name: "Claude Opus 4.7",
    provider: "Anthropic",
    brand: "claude",
    family: "Claude",
    capabilities: ["reasoning", "analysis"],
    popular: true,
  },
  {
    name: "Claude Opus 4.6",
    provider: "Anthropic",
    brand: "claude",
    family: "Claude",
    capabilities: ["reasoning", "analysis"],
  },
  {
    name: "Claude Sonnet 4.6",
    provider: "Anthropic",
    brand: "claude",
    family: "Claude",
    capabilities: ["analysis", "tool calling"],
  },
  {
    name: "Claude Haiku 4.5",
    provider: "Anthropic",
    brand: "claude",
    family: "Claude",
    capabilities: ["fast", "tool calling"],
  },
];

export const MODEL_PROVIDER_STRIP: { name: string; brand: BrandName }[] = [
  { name: "OpenAI", brand: "openai" },
  { name: "Anthropic", brand: "anthropic" },
  { name: "Google Gemini", brand: "gemini" },
  { name: "DeepSeek", brand: "deepseek" },
  { name: "Moonshot Kimi", brand: "moonshot" },
  { name: "xAI Grok", brand: "xai" },
];

const MODEL_ALIASES: Record<string, string[]> = {
  "Gemini 3.1 Pro Preview": ["Gemini 3.1 Pro"],
  "Grok 4.1 Fast (Reasoning)": ["Grok 4.1 Reasoning"],
  "GPT-OSS 120B": ["GPT OSS 120B"],
};

function normalizeModelName(model: string): string {
  return model.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function getModelMetadata(
  modelName: string | null,
): PublicChatModel | undefined {
  if (!modelName) return undefined;

  const normalized = normalizeModelName(modelName);
  return AVAILABLE_CHAT_MODELS.find((model) => {
    if (normalizeModelName(model.name) === normalized) return true;
    return (MODEL_ALIASES[model.name] || []).some(
      (alias) => normalizeModelName(alias) === normalized,
    );
  });
}

export function getModelCountsForWorkers(
  workerList: PublicWorker[],
): { model: string; count: number }[] {
  const counts = new Map<string, number>();
  workerList.forEach((worker) => {
    if (!worker.model) return;
    counts.set(worker.model, (counts.get(worker.model) || 0) + 1);
  });

  return [...counts.entries()]
    .map(([model, count]) => ({ model, count }))
    .sort((a, b) => b.count - a.count || a.model.localeCompare(b.model));
}

// All public workers, pre-sorted by execution count (most active first)
export const workers: PublicWorker[] = workersData as unknown as PublicWorker[];

// All public teams, pre-sorted by execution count
export const teams: PublicTeam[] = teamsData as unknown as PublicTeam[];

// Unique categories for filtering
export const categories: string[] = [
  ...new Set(workers.map((w) => w.category)),
].sort();

// Unique departments for filtering
export const departments: string[] = [
  ...new Set(workers.map((w) => w.department)),
].sort();

// Lookup a single worker by slug
export function getWorkerBySlug(slug: string): PublicWorker | undefined {
  return workers.find((w) => w.slug === slug);
}

// Lookup a single team by slug
export function getTeamBySlug(slug: string): PublicTeam | undefined {
  return teams.find((t) => t.slug === slug);
}

// Get top N workers by execution count
export function getTopWorkers(n: number): PublicWorker[] {
  return workers
    .filter((w) => w.stats && w.stats.totalExecutions > 0)
    .slice(0, n);
}

// Get workers by category
export function getWorkersByCategory(category: string): PublicWorker[] {
  return workers.filter((w) => w.category === category);
}

// Get team members as PublicWorker objects
export function getTeamMembers(team: PublicTeam): PublicWorker[] {
  return team.members
    .map((slug) => workers.find((w) => w.slug === slug))
    .filter(Boolean) as PublicWorker[];
}
